/* Twelve months at once.
 *
 * The furthest of the four distances, and the one with the least to say per
 * day — on purpose. A year is not for reading what you are doing; it is for
 * seeing *shape*. Which fortnight vanished. Whether the summer had anything in
 * it that was not work. When the run of empty weekends started.
 *
 * So a day here is a single mark whose weight is how full it was, and the only
 * things named are the ones that come round every year: birthdays,
 * anniversaries, the day somebody died. Which is, in practice, the reason
 * anybody opens a year view at all — nobody scrolls back twelve months to
 * check a Tuesday.
 *
 * ACCESSIBILITY. Twelve grids would be twelve tab stops and 365 more, so the
 * whole year is one `grid` with a single tab stop, arrow keys within it, and a
 * spoken summary per day. A calendar that a keyboard user has to tab through
 * day by day is a calendar they leave.
 */

import { InfoToggle } from '../ui/Section';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Occasion, YearSummary } from '@shared/types';
import { get, del, errorMessage } from '../api';
import { useSession, localToday } from '../session';
import ViewSwitch from '../ui/ViewSwitch';
import Icon from '../ui/Icon';
import OccasionForm from '../ui/OccasionForm';
import { ConfirmDialog } from '../ui/schedule';
import { useToast } from '../ui/toast';
import { hours } from '../ui/time';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

/* The same scale the month view uses, so a day does not change weight when you
   zoom out. Ten hours is a long day including the evening. */
const FULL_DAY_MIN = 10 * 60;

const pad = (n: number) => String(n).padStart(2, '0');
const isLeap = (y: number) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
const daysIn = (y: number, m: number) =>
  [31, isLeap(y) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1];

/** Monday = 0, so a column means the same thing in every month. */
const weekdayOf = (date: string) =>
  (new Date(date + 'T12:00:00Z').getUTCDay() + 6) % 7;

/** A month's own shape, in one glyph: how many of its days had anything on
    them. Not a number — a year of numbers is a spreadsheet. */
function monthLoad(year: number, month: number,
  days: Record<string, { plannedMin: number; lifeMin: number }>): string {
  const total = daysIn(year, month);
  let busy = 0;
  for (let i = 1; i <= total; i++)
    if (days[`${year}-${pad(month)}-${pad(i)}`]?.plannedMin) busy++;
  if (!busy) return '';
  const share = busy / total;
  return share > 0.6 ? '●●●' : share > 0.3 ? '●●' : '●';
}

const KIND_MARK: Record<string, string> = {
  birthday: '🎂', anniversary: '❤', memorial: '🕯', other: '★'
};

export default function YearScreen() {
  const { user, today, loading } = useSession();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const realToday = today ?? localToday(user?.tz);

  /* The year follows whatever date was being looked at elsewhere, so zooming
     out from a day in 2027 shows 2027 rather than now. */
  const asked = params.get('date');
  const anchor = asked && /^\d{4}-\d{2}-\d{2}$/.test(asked) ? asked : realToday;
  const [year, setYear] = useState(() => Number(anchor.slice(0, 4)));

  const [cursor, setCursor] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  /* One value: adding, editing a particular one, or removing one. Two pieces
     of state would let the screen ask two questions at once. */
  const [asking, setAsking] = useState<
    { kind: 'new' | 'edit' | 'remove'; occasion?: Occasion } | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['year'] });
    void qc.invalidateQueries({ queryKey: ['month'] });
    void qc.invalidateQueries({ queryKey: ['week'] });
  };

  const remove = useMutation({
    mutationFn: async (o: Occasion) => {
      const r = await del(`/api/occasions/${o.id}`);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not remove that.'));
      return o;
    },
    onSuccess: o => { refresh(); toast.say(`Removed “${o.title}”.`); },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const summary = useQuery({
    queryKey: ['year', year],
    enabled: !loading,
    queryFn: async () => {
      const r = await get<YearSummary>(`/api/plan/year?year=${year}`);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not load that year.'));
      return r;
    }
  });

  useEffect(() => {
    document.title = `${year} · Amon`;
    return () => { document.title = 'Amon'; };
  }, [year]);

  /* Land the cursor on today when today is in this year, and on 1 January
     otherwise — which is where somebody's eye goes anyway. */
  useEffect(() => {
    setCursor(prev => {
      if (prev && prev.startsWith(String(year))) return prev;
      return realToday.startsWith(String(year)) ? realToday : `${year}-01-01`;
    });
  }, [year, realToday]);

  const days = summary.data?.days ?? {};
  const occasions = summary.data?.occasions ?? {};

  const totals = useMemo(() => {
    const entries = Object.values(days);
    return {
      planned: entries.reduce((a, d) => a + d.plannedMin, 0),
      life: entries.reduce((a, d) => a + d.lifeMin, 0),
      busyDays: entries.filter(d => d.plannedMin > 0).length,
      lifeDays: entries.filter(d => d.lifeMin > 0).length,
      occasions: Object.values(occasions).flat().length
    };
  }, [days, occasions]);

  /* The next few that come round, which is what the list beside a year is
     actually for. Sorted by the server; sliced here. */
  const upcoming = useMemo(() => {
    const seen = new Map<string, Occasion>();
    for (const list of Object.values(occasions)) for (const o of list) seen.set(o.id, o);
    return [...seen.values()].sort((a, b) => a.daysAway - b.daysAway).slice(0, 6);
  }, [occasions]);

  function move(from: string, by: number) {
    const d = new Date(from + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() + by);
    const next = d.toISOString().slice(0, 10);
    const nextYear = Number(next.slice(0, 4));
    if (nextYear !== year) setYear(nextYear);
    setCursor(next);
    requestAnimationFrame(() => {
      gridRef.current?.querySelector<HTMLElement>(`[data-date="${next}"]`)?.focus();
    });
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!cursor) return;
    const by = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 7, ArrowUp: -7 }[e.key];
    if (by !== undefined) { e.preventDefault(); move(cursor, by); return; }
    if (e.key === 'PageDown') { e.preventDefault(); setYear(y => y + 1); return; }
    if (e.key === 'PageUp') { e.preventDefault(); setYear(y => y - 1); return; }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      /* Into the month rather than the day: from this far out, the next
         useful distance is the one below, not the one at the bottom. */
      setParams({ date: cursor });
      navigate(`/month?date=${cursor}`);
    }
  }

  return (
    <div className="screen">
      <ViewSwitch />

      <header className="topbar">
        <div>
          <h1>{year}</h1>
          <p className="muted">
            {summary.data
              ? `${hours(totals.planned)} planned across ` +
                `${totals.busyDays} ${totals.busyDays === 1 ? 'day' : 'days'}` +
                (totals.life ? ` · ${hours(totals.life)} of it yours` : '') +
                (totals.occasions ? ` · ${totals.occasions} to remember` : '')
              : 'twelve months, one mark a day'}
          </p>
        </div>
        <div className="inline">
          <button className="btn btn-ghost btn-icon" onClick={() => setYear(y => y - 1)}
                  aria-label="Previous year" title="Previous year"><Icon name="left" /></button>
          <button className="btn btn-ghost btn-sm" onClick={() => setYear(Number(realToday.slice(0, 4)))}>
            <Icon name="today" size={16} />This year
          </button>
          <button className="btn btn-ghost btn-icon" onClick={() => setYear(y => y + 1)}
                  aria-label="Next year" title="Next year"><Icon name="right" /></button>
        </div>
      </header>

      {summary.isError && (
        <div className="notice notice-bad" role="alert">
          <p><strong>Could not load {year}.</strong></p>
          <p className="muted small">{(summary.error as Error).message}</p>
          <button className="btn btn-ghost btn-sm" onClick={() => summary.refetch()}>Try again</button>
        </div>
      )}

      {upcoming.length > 0 && (
        <section className="panel" aria-labelledby="coming-round">
          <div className="panel-head">
            <h2 id="coming-round">Coming round</h2>
            <span className="muted small">the dates that repeat</span>
          </div>
          <ul className="occasion-strip">
            {upcoming.map(o => (
              <li key={o.id}>
                <span aria-hidden="true">{KIND_MARK[o.kind] ?? '★'}</span>
                <strong>{o.title}</strong>
                <span className="muted small">
                  {o.when}
                  {o.turning ? ` · turning ${o.turning}` : ''}
                  {o.shifted ? ' · shown on the 28th this year' : ''}
                </span>
                <button className="btn btn-ghost btn-sm"
                        onClick={() => setAsking({ kind: 'edit', occasion: o })}>Edit</button>
                <button className="btn btn-ghost btn-sm"
                        onClick={() => setAsking({ kind: 'remove', occasion: o })}>Remove</button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="month-hint">
        <span className="legend"><i className="legend-dot legend-life" />time for yourself</span>
        <span className="legend"><i className="legend-dot legend-full" />a fuller day</span>
        <InfoToggle about="the year"
                    text={<>Arrow keys move a day, <kbd>Enter</kbd> opens that month, <kbd>PgUp</kbd>/<kbd>PgDn</kbd>
                      {' '}change year. A darker mark is a fuller day; a green one had time in it that was not work.</>} />
      </div>

      <div className="yeargrid" role="grid" aria-label={`${year}, twelve months`}
           ref={gridRef} onKeyDown={onKeyDown}>
        {MONTHS.map((name, mi) => {
          const month = mi + 1;
          const first = `${year}-${pad(month)}-01`;
          const lead = weekdayOf(first);
          const count = daysIn(year, month);

          return (
            <div key={name} className="yearmonth" role="rowgroup">
              {/* The month is the way in. A year is where you notice that
                  October is full; the next thing you want is October, not a
                  link buried in a heading. */}
              <h3 className="yearmonth-name">
                <button className="monthjump"
                        onClick={() => navigate(`/month?date=${first}`)}
                        aria-label={`Open ${name} ${year}`}>
                  <span className="monthjump-name">{name}</span>
                  <span className="monthjump-load" aria-hidden="true">
                    {monthLoad(year, month, days)}
                  </span>
                </button>
              </h3>
              <div className="yearmonth-days" role="row">
                {Array.from({ length: lead }, (_, i) => (
                  <span key={`pad${i}`} className="yearday is-blank" aria-hidden="true" />
                ))}
                {Array.from({ length: count }, (_, i) => {
                  const date = `${year}-${pad(month)}-${pad(i + 1)}`;
                  const d = days[date];
                  const marks = occasions[date] ?? [];
                  const load = Math.min(1, (d?.plannedMin ?? 0) / FULL_DAY_MIN);
                  const spoken = [
                    new Intl.DateTimeFormat(undefined,
                      { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' })
                      .format(new Date(date + 'T12:00:00Z')),
                    date === realToday ? 'today' : '',
                    d?.plannedMin ? `${hours(d.plannedMin)} planned` : 'nothing planned',
                    d?.lifeMin ? `${hours(d.lifeMin)} for yourself` : '',
                    ...marks.map(o => o.title)
                  ].filter(Boolean).join(', ');

                  return (
                    <button
                      key={date}
                      type="button"
                      role="gridcell"
                      data-date={date}
                      tabIndex={cursor === date ? 0 : -1}
                      aria-label={spoken}
                      aria-current={date === realToday ? 'date' : undefined}
                      onFocus={() => setCursor(date)}
                      onClick={() => navigate(`/day?date=${date}`)}
                      className={`yearday${date === realToday ? ' is-today' : ''}` +
                        `${marks.length ? ' has-occasion' : ''}` +
                        `${d?.lifeMin ? ' has-life' : ''}`}
                      style={load ? { '--load': load } as React.CSSProperties : undefined}>
                      <span aria-hidden="true">{i + 1}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {(asking?.kind === 'new' || asking?.kind === 'edit') && (
        <OccasionForm
          existing={asking.occasion}
          onClose={() => setAsking(null)}
          onSaved={o => {
            refresh();
            toast.say(`“${o.title}” is ${o.when}${o.turning ? ` — turning ${o.turning}` : ''}.`,
              'good');
          }} />
      )}

      {asking?.kind === 'remove' && asking.occasion && (
        <ConfirmDialog
          title={`Remove “${asking.occasion.title}”?`}
          description="It stops appearing on your calendar, and Amon stops telling you it is coming."
          confirmLabel="Remove it"
          danger
          onClose={() => setAsking(null)}
          onConfirm={() => remove.mutateAsync(asking.occasion!)} />
      )}
    </div>
  );
}
